<script setup lang="ts">
import {
  findTreeViewDataObjectById,
  type TreeViewDataObject,
  type TreeViewItemDropdownAction,
} from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { useToggle } from "~/core/composables/useToggle";
import { eventBus } from "~/core/services/eventBus";
import { PATH_SECTORS, PATH_SECTORS_CODE } from "~/modules/sector/constants";
import { DropdownActions } from "~/modules/sector/models/DropdownActions.enum";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { findSectorInSectorTree } from "~/modules/sector/utils/findSectorInSectorTree";
import { findSectorParentInSectorTree } from "~/modules/sector/utils/findSectorParentInSectorTree";
import { mapSectorsToTreeViewDataObjects } from "~/modules/sector/utils/mapSectorsToTreeViewDataObjects";
import { router } from "~/plugins/router";

const { sectors, availableSectors } = storeToRefs(useSectorStore());
const route = useRoute();
const [formDialogVisible, toggleFormDialogVisible] = useToggle();
const [removeDialogVisible, toggleRemoveDialogVisible] = useToggle();

const { t } = useI18n();

const selectedSector = ref<Sector | undefined>();
const selectedTreeViewSector = defineModel<TreeViewDataObject>();
const sectorsTreeViewDataObjects: ComputedRef<TreeViewDataObject[]> = computed(
  () => {
    return sectors.value
      ? mapSectorsToTreeViewDataObjects(sectors.value, t)
      : [];
  },
);
const parentSectorCode: Ref<number | undefined> = ref();

// Case when accessing the page directly from URL
if (route.params.code) {
  selectSectorByCode(Number(route.params.code));
} else {
  selectedTreeViewSector.value = undefined;
}

/**
 * Select a sector as TreeViewDataObject by its code
 * @param sectorCode
 */
function selectSectorByCode(sectorCode: number) {
  const foundSector = findTreeViewDataObjectById(
    sectorsTreeViewDataObjects.value,
    sectorCode,
  );

  if (foundSector && !foundSector.isDisabled) {
    selectedTreeViewSector.value = foundSector;
  }
}

/**
 * On select a specific sector => redirect to the sector route
 * @param sector
 */
function onSelectTreeViewSector(sector: TreeViewDataObject) {
  router.push({ name: PATH_SECTORS_CODE, params: { code: sector.id } });
}

function setSelectedSector(sectorCode?: number) {
  if (sectorCode && sectors.value) {
    selectedSector.value = findSectorInSectorTree(sectors.value, sectorCode);
  } else {
    selectedSector.value = undefined;
  }
}

const handleSectorAction = ({
  item,
  action,
}: {
  item: TreeViewDataObject;
  action: TreeViewItemDropdownAction;
}) => {
  switch (action.id) {
    case DropdownActions.CREATE:
      onCreateSector(item);
      break;
    case DropdownActions.UPDATE:
      onUpdateSector(item.id as number);
      break;
    case DropdownActions.REMOVE:
      onRemoveSector(item.id as number);
      break;
  }
};

function onCreateSector(sector: TreeViewDataObject) {
  setSelectedSector();
  parentSectorCode.value = sector.id as number;
  toggleFormDialogVisible();
}

function onUpdateSector(sectorCode: number) {
  setSelectedSector(sectorCode);
  if (sectors.value) {
    parentSectorCode.value = findSectorParentInSectorTree(
      sectors.value,
      sectorCode,
    )?.code;
  }
  toggleFormDialogVisible();
}

async function onRemoveSector(sectorCode: number) {
  setSelectedSector(sectorCode);
  if (sectors.value) {
    parentSectorCode.value = findSectorParentInSectorTree(
      sectors.value,
      sectorCode,
    )?.code;
  }
  toggleRemoveDialogVisible();
}

function onChangeSectorForm() {
  if (parentSectorCode.value) {
    eventBus.emit("sector", {
      parentSectorCode: parentSectorCode.value,
      sectorCode: selectedSector.value?.code,
    });
  }
  onCloseSectorForm();
}

function onCloseSectorForm() {
  toggleFormDialogVisible(false);
  setSelectedSector();
}

function onCloseSectorRemove(isSectorRemoved?: boolean) {
  toggleRemoveDialogVisible(false);

  if (
    isSectorRemoved &&
    selectedSector.value?.code === Number(route.params.code)
  ) {
    router.push({ name: PATH_SECTORS });
  }

  setSelectedSector();
}

watch(
  () => selectedTreeViewSector.value,
  (newSelection) => {
    if (newSelection !== undefined) {
      onSelectTreeViewSector(newSelection);
    }
  },
);

watch(
  () => route.params.code as string | undefined,
  (sectorCode: string | undefined) => {
    if (sectorCode) {
      selectSectorByCode(Number(sectorCode));
    } else {
      selectedTreeViewSector.value = undefined;
    }
  },
);
</script>

<template>
  <div class="flex-none bg-gray-50 py-8 px-2 w-64">
    <container-component :px="0" :py="0">
      <lxc-tree-view
        v-model="selectedTreeViewSector"
        :data="sectorsTreeViewDataObjects"
        handle-select
        menu-style
        :action-title="t('sectors.treeview.actionsButton.title')"
        @dropdown-action="handleSectorAction"
      />

      <sector-form
        v-model:show="formDialogVisible"
        :parent-sector-code="parentSectorCode"
        :sector="selectedSector"
        @close="onCloseSectorForm"
        @change="onChangeSectorForm"
      />

      <sector-remove
        :is-visible="removeDialogVisible"
        :sector="selectedSector"
        @close="onCloseSectorRemove()"
        @change="onCloseSectorRemove(true)"
      />
    </container-component>
  </div>
</template>
